import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Check, KeyRound, Ban, MapPin, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';
import { visibleInterval } from '@/lib/visibleInterval';

const errText = e => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x.msg).join(' ');
  return 'Something went wrong';
};
const card = 'bg-white border border-[#E2E2DF] rounded-md p-6';
const NUM_FIELDS = [
  ['idle_minutes', 'Log out after inactivity (minutes)', 5, 240],
  ['session_hours', 'Maximum session length (hours)', 1, 24],
  ['location_interval_minutes', 'Record location every (minutes)', 1, 120],
  ['location_grace_minutes', 'Log out if location lost for (minutes)', 1, 30],
];
const TOGGLES = [
  ['require_location', 'Require location to log in and stay logged in'],
  ['require_device_approval', 'New devices need approval'],
];

function SettingsCard({ api }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/security-settings').then(r => setS(r.data.settings)).catch(e => toast.error(errText(e))); }, [api]);
  if (!s) return <div className={card}>Loading…</div>;
  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/admin/security-settings', s); setS(r.data.settings); toast.success('Security settings saved. They apply to new logins and on each check.'); }
    catch (e) { toast.error(errText(e)); }
    setSaving(false);
  };
  return (
    <div className={card} data-testid="security-settings-card">
      <h2 className="text-lg font-bold text-slate-900 mb-4">Session &amp; location rules</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {NUM_FIELDS.map(([k, label, min, max]) => (
          <label key={k} className="text-sm text-slate-700 space-y-1">
            <span className="block">{label}</span>
            <input data-testid={`security-setting-${k}`} type="number" min={min} max={max} value={s[k]}
              onChange={e => setS({ ...s, [k]: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D35400]/30" />
          </label>
        ))}
      </div>
      <div className="mt-5 space-y-3">
        {TOGGLES.map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 text-sm text-slate-700">
            <Switch data-testid={`security-setting-${k}`} checked={!!s[k]} onCheckedChange={v => setS({ ...s, [k]: v })} />{label}
          </label>
        ))}
      </div>
      <button data-testid="security-settings-save-btn" onClick={save} disabled={saving}
        className="mt-6 px-5 py-2 rounded-md bg-[#D35400] hover:bg-[#A04000] text-white text-sm font-semibold transition-colors disabled:opacity-50">
        {saving ? 'Saving…' : 'Save settings'}</button>
    </div>
  );
}

const fmtDate = v => (v ? new Date(v.endsWith?.('Z') || v.includes?.('+') ? v : `${v}Z`).toLocaleString('en-NZ') : '—');
const STATUS_CLS = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', revoked: 'bg-slate-200 text-slate-600' };

function DeviceRow({ d, onAction, code }) {
  const loc = d.last_location;
  return (
    <tr className="border-t border-[#E2E2DF]" data-testid={`device-row-${d._id}`}>
      <td className="py-3 pr-3"><div className="font-medium text-slate-900">{d.user_name || d.user_email}</div><div className="text-xs text-slate-500">{d.user_email}</div></td>
      <td className="py-3 pr-3"><div>{d.label}</div><div className="text-xs text-slate-500">{d.ip}</div></td>
      <td className="py-3 pr-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_CLS[d.status] || ''}`} data-testid={`device-status-${d._id}`}>{d.status}</span></td>
      <td className="py-3 pr-3 text-xs text-slate-600">{fmtDate(d.last_seen || d.requested_at)}
        {loc && <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#D35400] hover:underline" data-testid={`device-map-${d._id}`}><MapPin size={12} />View on map</a>}</td>
      <td className="py-3 text-right whitespace-nowrap space-x-2">
        {d.status === 'pending' && <>
          <button data-testid={`device-approve-${d._id}`} onClick={() => onAction(d, 'approve')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors"><Check size={14} />Approve</button>
          <button data-testid={`device-code-${d._id}`} onClick={() => onAction(d, 'code')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E2E2DF] hover:bg-slate-50 text-xs transition-colors"><KeyRound size={14} />Give code</button>
        </>}
        {d.status !== 'revoked' && <button data-testid={`device-revoke-${d._id}`} onClick={() => onAction(d, 'revoke')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs transition-colors"><Ban size={14} />{d.status === 'pending' ? 'Decline' : 'Remove'}</button>}
        {code && <div className="mt-2 text-sm" data-testid={`device-code-value-${d._id}`}>Code: <b className="font-mono tracking-widest text-lg">{code}</b> <span className="text-xs text-slate-500">(10 min)</span></div>}
      </td>
    </tr>
  );
}

function DevicesCard({ api }) {
  const [filter, setFilter] = useState('pending');
  const [devices, setDevices] = useState([]);
  const [codes, setCodes] = useState({});
  const load = useCallback(() => {
    api.get('/admin/devices', { params: filter === 'all' ? {} : { status: filter } }).then(r => setDevices(r.data.devices)).catch(e => toast.error(errText(e)));
  }, [api, filter]);
  useEffect(() => { load(); return visibleInterval(load, 15000); }, [load]);
  const onAction = async (d, action) => {
    if (action === 'revoke' && !window.confirm(`Remove ${d.label} for ${d.user_email}? They will be logged out on that device.`)) return;
    try {
      const r = await api.post(`/admin/devices/${d._id}/${action}`);
      if (action === 'code') setCodes({ ...codes, [d._id]: r.data.code });
      else { toast.success(action === 'approve' ? 'Device approved' : 'Device removed'); load(); }
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className={card} data-testid="devices-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-slate-900">Devices</h2>
        <div className="flex gap-1">
          {['pending', 'approved', 'revoked', 'all'].map(f => (
            <button key={f} data-testid={`devices-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{f}</button>
          ))}
          <button data-testid="devices-refresh-btn" onClick={load} className="px-2 text-slate-500 hover:text-slate-900"><RefreshCw size={14} /></button>
        </div>
      </div>
      {devices.length === 0 ? <p className="text-sm text-slate-500" data-testid="devices-empty">No {filter === 'all' ? '' : filter} devices.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500"><th className="pb-2">User</th><th className="pb-2">Device</th><th className="pb-2">Status</th><th className="pb-2">Last seen</th><th /></tr></thead>
          <tbody>{devices.map(d => <DeviceRow key={d._id} d={d} onAction={onAction} code={codes[d._id]} />)}</tbody>
        </table></div>
      )}
    </div>
  );
}

export default function SecuritySettingsPage() {
  const { api, user } = useAuth();
  if (user?.role !== 'admin') return <p className="text-slate-600" data-testid="security-admin-only">This page is for admins only.</p>;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="security-settings-page">
      <div className="flex items-center gap-3"><ShieldCheck className="text-[#D35400]" />
        <div><h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Security</h1>
          <p className="text-sm text-slate-500">Approve devices and set session and location rules.</p></div></div>
      <DevicesCard api={api} />
      <SettingsCard api={api} />
    </div>
  );
}
